import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import bdgWave from '../../assets/intro/bdg-wave.webp'
import bdgCenter from '../../assets/intro/bdg-center-icon.webp'
import {
  createTextProvider,
  executeAction,
  VoiceClient,
  isVoiceSupported,
  speak,
  stopSpeaking,
  subscribeVoiceLevel,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
} from '../../services/assistant'
import type { ChatMessage, VoiceState, AssistantAction } from '../../services/assistant'
import 'flag-icons/css/flag-icons.min.css'
import './ChatWidget.css'

let idCounter = 0
const nextId = () => `m${Date.now()}-${idCounter++}`

const GREETING: ChatMessage = {
  id: 'greeting',
  role: 'assistant',
  text: "Hi! 👋 I'm the BigData Ghana assistant. Type a question, or tap the mic to talk. Try \"what service fits my business?\" or \"show me the capabilities section\".",
  createdAt: Date.now(),
}

const VOICE_LABEL: Record<VoiceState, string> = {
  idle: '',
  connecting: 'Connecting…',
  listening: 'Listening…',
  thinking: 'Thinking…',
  speaking: 'Speaking…',
  error: 'Voice unavailable',
}

function ChatWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING])
  const [input, setInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const [voiceState, setVoiceState] = useState<VoiceState>('idle')
  const [voiceChoice, setVoiceChoice] = useState<'female' | 'male'>('female')
  const [language, setLanguage] = useState<string>(DEFAULT_LANGUAGE)
  const [langOpen, setLangOpen] = useState(false)
  const [viewportHeight, setViewportHeight] = useState<number | null>(null)

  const navigate = useNavigate()
  const location = useLocation()

  const provider = useMemo(() => createTextProvider(), [])
  const sessionId = useMemo(() => nextId(), [])

  const bodyRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const voiceViewRef = useRef<HTMLDivElement>(null)
  const voiceRef = useRef<VoiceClient | null>(null)
  const pathRef = useRef(location.pathname)
  const busyRef = useRef(false)
  const voiceChoiceRef = useRef(voiceChoice)
  const languageRef = useRef(language)
  useEffect(() => {
    pathRef.current = location.pathname
  }, [location.pathname])
  useEffect(() => {
    voiceChoiceRef.current = voiceChoice
  }, [voiceChoice])
  useEffect(() => {
    languageRef.current = language
  }, [language])

  useEffect(() => {
    if (!window.visualViewport) return
    const update = () => setViewportHeight(window.visualViewport!.height)
    window.visualViewport.addEventListener('resize', update)
    window.visualViewport.addEventListener('scroll', update)
    update()
    return () => {
      window.visualViewport!.removeEventListener('resize', update)
      window.visualViewport!.removeEventListener('scroll', update)
    }
  }, [])

  useEffect(() => {
    const el = bodyRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, thinking])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    return () => {
      voiceRef.current?.stop()
      stopSpeaking()
    }
  }, [])

  // Feed the assistant's live speaking amplitude into the voice view as a CSS
  // variable so the rings react to the actual voice instead of a fixed timer.
  useEffect(() => {
    return subscribeVoiceLevel((level) => {
      voiceViewRef.current?.style.setProperty('--voice-level', level.toFixed(3))
    })
  }, [])

  function closePanel() {
    // Stop any voice session and audio when the chat is dismissed.
    voiceRef.current?.stop()
    voiceRef.current = null
    stopSpeaking()
    setOpen(false)
  }

  function pushMessage(role: 'user' | 'assistant', text: string, action?: AssistantAction) {
    setMessages((prev) => {
      const last = prev[prev.length - 1]
      if (last && last.role === role && last.text === text) return prev
      return [...prev, { id: nextId(), role, text, action, createdAt: Date.now() }]
    })
  }

  function runAction(action: AssistantAction) {
    executeAction(action, { navigate, currentPath: pathRef.current })
  }

  const voiceActive = voiceState !== 'idle' && voiceState !== 'error'

  /** Send a user message to the brain, show the reply, act, and optionally speak. */
  async function askAndRespond(text: string, shouldSpeak: boolean) {
    if (busyRef.current) {
      console.debug('[voice] askAndRespond ignored — already busy')
      return
    }
    busyRef.current = true
    setThinking(true)
    if (shouldSpeak) voiceRef.current?.setBusy('thinking')
    console.debug('[voice] askAndRespond start:', text)

    try {
      const reply = await provider.send(text, sessionId, languageRef.current)
      pushMessage('assistant', reply.text, reply.action)
      if (reply.action) runAction(reply.action)

      if (shouldSpeak) {
        // Pause the mic while the assistant talks so it doesn't hear itself.
        voiceRef.current?.pause()
        voiceRef.current?.setBusy('speaking')
        try {
          await speak(reply.text, voiceChoiceRef.current, languageRef.current)
        } catch {
          /* audio failed; keep the text reply */
        }
      }
    } catch {
      pushMessage('assistant', 'Sorry, something went wrong on my end. Please try again.')
    } finally {
      // ALWAYS clear busy and resume the mic, even if the brain call or speech
      // threw or timed out — otherwise the widget gets stuck after one turn.
      setThinking(false)
      busyRef.current = false
      if (shouldSpeak) voiceRef.current?.resume()
      console.debug('[voice] askAndRespond done — mic resumed')
    }
  }

  async function handleSend() {
    const text = input.trim()
    if (!text || thinking) return
    setInput('')
    pushMessage('user', text)
    await askAndRespond(text, voiceActive)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function toggleVoice() {
    if (voiceActive) {
      voiceRef.current?.stop()
      voiceRef.current = null
      stopSpeaking()
      return
    }

    if (!isVoiceSupported()) {
      pushMessage('assistant', 'Voice input needs Chrome or Edge. You can keep chatting by text.')
      return
    }

    const client = new VoiceClient(
      {
        onState: setVoiceState,
        onUserUtterance: (text) => {
          pushMessage('user', text)
          void askAndRespond(text, true)
        },
        onError: (message) => {
          pushMessage('assistant', message)
        },
      },
      languageRef.current,
    )
    voiceRef.current = client
    client.start()
  }

  function changeLanguage(code: string) {
    setLangOpen(false)
    if (code === language) return
    setLanguage(code)
    languageRef.current = code
    // If a voice session is live, restart it so speech-to-text switches to the
    // new language immediately (the language is fixed at connection time).
    if (voiceActive) {
      voiceRef.current?.stop()
      stopSpeaking()
      const client = new VoiceClient(
        {
          onState: setVoiceState,
          onUserUtterance: (text) => {
            pushMessage('user', text)
            void askAndRespond(text, true)
          },
          onError: (message) => {
            pushMessage('assistant', message)
          },
        },
        code,
      )
      voiceRef.current = client
      client.start()
    }
  }

  const [showContactForm, setShowContactForm] = useState(false)
  const [contactFormStatus, setContactFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle')

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setContactFormStatus('submitting')
    setTimeout(() => {
      setContactFormStatus('success')
      setTimeout(() => {
        setShowContactForm(false)
        setContactFormStatus('idle')
      }, 2000)
    }, 1000)
  }

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language) ?? SUPPORTED_LANGUAGES[0]

  return (
    <>
      <button
        className={`chat-widget ${open ? 'chat-widget--open' : ''}`}
        onClick={() => (open ? closePanel() : setOpen(true))}
        aria-label={open ? 'Close chat' : 'Chat with us'}
      >
        <svg className="chat-widget__ring-text" viewBox="0 0 100 100">
          <defs>
            <path id="circlePath" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
          </defs>
          <text>
            <textPath href="#circlePath" className="chat-widget__text-path">
              contact · contact · contact · 
            </textPath>
          </text>
        </svg>
        <div className="chat-widget__avatar">
          <img src={bdgWave} alt="" className="chat-widget__wave" />
          <img src={bdgCenter} alt="" className="chat-widget__satellite" />
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="chat-panel"
            role="dialog"
            aria-label="BigData Ghana assistant"
            style={{ '--vv-height': viewportHeight ? `${viewportHeight}px` : undefined } as React.CSSProperties}
          >
          <div className="chat-panel__header">
            <div className="chat-panel__brand">
              <span className="chat-panel__brand-mark">
                <img src={bdgWave} alt="" className="chat-panel__brand-wave" />
                <img src={bdgCenter} alt="" className="chat-panel__brand-dot" />
              </span>
              <span className="chat-panel__title">BigData Ghana Assistant</span>
            </div>
            
            <div className="chat-panel__header-actions">
              <button 
                className="chat-panel__subscribe-btn"
                onClick={() => setShowContactForm(!showContactForm)}
                title="Leave your details for updates"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </button>
              
              <div className="chat-lang">
                <button
                  className="chat-lang__btn"
                  onClick={() => setLangOpen((o) => !o)}
                  aria-haspopup="listbox"
                  aria-expanded={langOpen}
                  title={`Language: ${currentLang.label}`}
                >
                  <span className={`fi fi-${currentLang.country} chat-lang__flag`} />
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {langOpen && (
                  <ul className="chat-lang__menu" role="listbox" aria-label="Language">
                    {SUPPORTED_LANGUAGES.map((l) => (
                      <li key={l.code}>
                        <button
                          className={`chat-lang__opt ${l.code === language ? 'is-active' : ''}`}
                          onClick={() => changeLanguage(l.code)}
                          role="option"
                          aria-selected={l.code === language}
                        >
                          <span className={`fi fi-${l.country} chat-lang__flag`} />
                          <span className="chat-lang__label">{l.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button className="chat-panel__close" onClick={closePanel} aria-label="Close">
                ×
              </button>
            </div>
          </div>

          <AnimatePresence>
            {showContactForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="chat-panel__contact-form-wrap"
              >
                {contactFormStatus === 'success' ? (
                  <div className="chat-panel__contact-success">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E8A00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <p>Thanks! We'll keep you updated.</p>
                  </div>
                ) : (
                  <form className="chat-panel__contact-form" onSubmit={handleContactSubmit}>
                    <p className="chat-panel__contact-intro">Leave your details to get updates, tips, and offers.</p>
                    <div className="chat-panel__contact-grid">
                      <input type="text" placeholder="Name" required className="chat-panel__contact-input" />
                      <input type="tel" placeholder="Phone (optional)" className="chat-panel__contact-input" />
                      <input type="email" placeholder="Email" required className="chat-panel__contact-input" />
                      <input type="text" placeholder="Message (optional)" className="chat-panel__contact-input" />
                    </div>
                    <button type="submit" className="chat-panel__contact-submit" disabled={contactFormStatus === 'submitting'}>
                      {contactFormStatus === 'submitting' ? 'Sending...' : 'Keep me updated'}
                    </button>
                  </form>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {voiceActive ? (
            <div className={`chat-voice chat-voice--${voiceState}`} ref={voiceViewRef}>
              <div className="chat-voice__stage" aria-hidden="true">
                <span className="chat-voice__reactive" />
                <div className="chat-voice__rings">
                  <span className="chat-voice__ring chat-voice__ring--1" />
                  <span className="chat-voice__ring chat-voice__ring--2" />
                  <span className="chat-voice__ring chat-voice__ring--3" />
                </div>
                <div className="chat-voice__mark">
                  <img className="chat-voice__wave" src={bdgWave} alt="" />
                </div>
              </div>

              <div className="chat-voice__status">{VOICE_LABEL[voiceState] || 'Voice'}</div>
              <div className="chat-voice__hint">
                {voiceState === 'listening' ? 'Speak naturally — I’m listening' : 'Talk with the assistant'}
              </div>

              <div className="chat-voice__picker" role="group" aria-label="Assistant voice">
                <button
                  className={`chat-panel__voice-opt ${voiceChoice === 'female' ? 'is-active' : ''}`}
                  onClick={() => setVoiceChoice('female')}
                >
                  Female
                </button>
                <button
                  className={`chat-panel__voice-opt ${voiceChoice === 'male' ? 'is-active' : ''}`}
                  onClick={() => setVoiceChoice('male')}
                >
                  Male
                </button>
              </div>

              <button className="chat-voice__end" onClick={toggleVoice} aria-label="End voice chat">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 15.46l-5.27-.61-2.52 2.52a15.05 15.05 0 0 1-6.59-6.59l2.53-2.53L8.54 3H3.03C2.45 13.18 10.82 21.55 21 20.97v-5.51z"
                    fill="#ffffff"
                    transform="rotate(135 12 12)"
                  />
                </svg>
                <span>End</span>
              </button>

              <div className="chat-voice__type">
                <input
                  type="text"
                  className="chat-voice__type-input"
                  placeholder="Or send a message…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className="chat-voice__type-send"
                  onClick={handleSend}
                  disabled={!input.trim() || thinking}
                  aria-label="Send"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" fill="#2ea872" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="chat-panel__body" ref={bodyRef}>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`chat-panel__msg chat-panel__msg--${msg.role === 'user' ? 'user' : 'bot'}`}
                  >
                    <p>{msg.text}</p>
                  </div>
                ))}
                {thinking && (
                  <div className="chat-panel__msg chat-panel__msg--bot">
                    <p className="chat-panel__typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </p>
                  </div>
                )}
              </div>

              <div className="chat-panel__input-wrap">
                <button
                  className="chat-panel__mic"
                  onClick={toggleVoice}
                  aria-label="Start voice chat"
                  title="Talk to the assistant"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" fill="#0f2b1f" />
                    <path
                      d="M19 11a7 7 0 0 1-14 0M12 18v3"
                      stroke="#0f2b1f"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                <textarea
                  ref={inputRef as any}
                  className="chat-panel__input"
                  placeholder="Type a message..."
                  value={input}
                  rows={1}
                  onChange={(e) => {
                    setInput(e.target.value)
                    // Auto-resize
                    e.target.style.height = 'auto'
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
                  }}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className="chat-panel__send"
                  onClick={handleSend}
                  disabled={!input.trim() || thinking}
                  aria-label="Send"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" fill="#2ea872" />
                  </svg>
                </button>
              </div>
            </>
          )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default ChatWidget
